/*
 * This file contains the logic for the subpoint-tags feature flag, which is
 * hacky code mostly isolated to this one file. This will all be deleted (and
 * replaced by a real product feature) once CDI41 completes in a few months.
 * Agreement with Anand/Megan:
 *
 * 1. I’ll isolate all the messy code to a single file, making sure there’s
 * nothing risky/dangerous in that code, and writing some basic test coverage.
 *
 * 2. That will make it ready to go into production, so CDI41 can use it. We
 * won’t use it for any other project, since it’s not robust across different
 * project configurations (for example, using it with rectangles will break
 * things).
 *
 * 3. We’re committing to deleting this code once the project wraps up in a few
 * months. After that, the goal is to fully develop the complete point-level
 * attributes epic, which will be flexible and usable by any client in the
 * future.
 *
 * 4. If for some reason we don’t wind up prioritizing the full epic, and we
 * wind up wanting to use this feature for another client as opposed to deleting
 * the code, before that can be done we’ll need to spend more work getting this
 * prototype code up to real production standards.
 */
/* eslint-disable max-lines -- See comment above */
/* eslint-disable max-lines-per-function -- See comment above */
/* eslint-disable max-nested-callbacks -- See comment above */
import { inject, Injectable, Injector } from '@angular/core';
import { mapAsKeys } from '@sama/js-core';
import {
  cloneDeep,
  flatten,
  get,
  isEqual,
  matches,
  noop,
  partition,
} from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import { values } from 'lodash';
import { combineLatest, merge, NEVER } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { ShapeLocationState } from '../../../cross-team-shared/annotation/shared/state/shape-location-state';
import { ShapeState } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { assertStore } from '../../../cross-team-shared/misc-utils/assert-store';
import { ShapeGroupState } from '../../../cross-team-shared/state/shape-group-state';
import { outputAnnotationInfoStoreToken } from '../../../outputs/annotation/annotation-shared/annotation-providers';
import { AbstractAnnotationStore } from '../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { PointType } from '../../../team-apis/otterdam/answers/shape-typings';
import { outputInfoStoreToken } from '../../h3ml/state/output-info';
import {
  fusionStoreToken,
  TaskWorkspaceStore,
} from '../../h3ml/state/task-workspace-store';
import { UndoService } from '../../outputs/annotation/annotation-shared/toolbars/static-toolbar/undo.service';
import { ObjectlessFocusedShapeService } from '../../outputs/annotation/annotation-shared/utils/objectless-focused-shape.service';
import { FeatureFlagService } from './feature-flag.service';
import { ObjectlessShapeService } from './objectless-shape.service';
import { ShapeGroupingService } from './shape-grouping.service';

@Injectable()
export class SubpointBindingService extends InjectableSuperclass {
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly focusedShapeService = inject(ObjectlessFocusedShapeService);
  private readonly fusionStore = inject(fusionStoreToken);
  private readonly outputInfoStore = inject(outputInfoStoreToken);
  private readonly shapeGroupingService = inject(ShapeGroupingService);
  private readonly outputAnnotationInfoStore = inject(
    outputAnnotationInfoStoreToken,
  );
  private readonly store = inject(AbstractAnnotationStore);
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);
  private readonly undoService = inject(UndoService);
  private objectlessShapeService = inject(ObjectlessShapeService);

  constructor(injector: Injector) {
    super(injector);

    if (this.featureFlagService.hasFlag('subpoint-tags')) {
      this.setUpSubscriptions();
    }
  }

  getGeneratePointsContextMenuOptions() {
    const groupTypes: string[] =
      get(this.outputInfoStore.state(), ['output', 'meta', 'group_types']) ||
      [];
    return {
      label: 'Generate points for subpoints',
      action: noop,
      hidden: this.taskWorkspaceStore.state().readOnly,
      children: groupTypes.map((groupType) => ({
        label: groupType,
        action: () => {
          const shape = this.focusedShapeService
            .getFocusedShapeStore()
            ?.state();

          if (!shape) {
            return;
          }

          this.createAndGroupPointsForShape(shape.index, groupType);
        },
      })),
    };
  }

  private setUpSubscriptions() {
    this.subscribeTo(
      combineLatest([
        this.store.ready$,
        this.fusionStore('syncSubpointShapes').$,
      ]).pipe(
        switchMap(([, syncSubpointShapes]) => {
          if (!syncSubpointShapes) {
            return NEVER;
          }

          return this.outputAnnotationInfoStore('groups').$.pipe(
            switchMap((groups) =>
              merge(
                ...values(groups).map((group) =>
                  // @ts-ignore -- fix me
                  this.updatePointsAndSubpoints$(group),
                ),
              ),
            ),
          );
        }),
      ),
    );
  }

  private createAndGroupPointsForShape(index: number, groupType: string) {
    const shape = this.getShapeStore(index).state();
    const pointShapes = this.createPointsForShape(shape);
    this.shapeGroupingService.groupShapes(
      groupType,
      mapAsKeys(
        [shape.index, ...pointShapes.map((pointShape) => pointShape.index)],
        () => true,
      ),
    );
    this.undoService.pushCurrentState();
  }

  private createPointsForShape(shape: ShapeState) {
    const maxSubpointCount = Math.max(
      ...shape.key_locations.map((k) => k.points.length),
    );
    return Array(maxSubpointCount)
      .fill(0)
      .map((_, subpointIndex) =>
        this.createPointForShape(shape, subpointIndex),
      );
  }

  private createPointForShape(shape: ShapeState, subpointIndex: number) {
    const pointShape = cloneDeep(shape);
    pointShape.index = this.taskWorkspaceStore.claimNextInternalId();
    pointShape.tags = {};
    pointShape.dynamic_tags = {};
    pointShape.type = 'point';
    pointShape.key_locations.forEach((keyLocation, keyLocationIndex) => {
      const point = shape.key_locations[keyLocationIndex].points[subpointIndex];
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      keyLocation.points = [point ?? [0, 0]];
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      keyLocation.visibility = point ? keyLocation.visibility : 0;
    });
    const outputName = this.outputInfoStore.state().output.name;
    this.objectlessShapeService.createShape(pointShape, outputName);
    return pointShape;
  }

  private updatePointsAndSubpoints$(group: ShapeGroupState) {
    const { shapes } = this.outputAnnotationInfoStore.state();
    const shapesInGroup = group.shapeIndexes.map((index) => shapes[index]);
    const [pointShapes, nonPointShapes] = partition(
      shapesInGroup,
      (shape) => shape.type === 'point',
    );

    if (nonPointShapes.length !== 1 || pointShapes.length === 0) {
      return NEVER;
    }

    // @ts-ignore -- fix me
    const nonPointShapeIndex = nonPointShapes[0].index;
    // @ts-ignore -- fix me
    const pointShapeIndices = pointShapes.map((shape) => shape.index);

    const createMissingPoints$ = this.createMissingPoints$(
      nonPointShapeIndex,
      pointShapeIndices,
      group.type,
    );
    const updatePoints$ = this.updatePoints$(
      nonPointShapeIndex,
      pointShapeIndices,
    );
    const updateSubpoints$ = this.updateSubpoints$(
      nonPointShapeIndex,
      pointShapeIndices,
    );
    return merge(createMissingPoints$, updatePoints$, updateSubpoints$);
  }

  private createMissingPoints$(
    nonPointShapeIndex: number,
    pointShapesIndexes: number[],
    groupType: string,
  ) {
    const shapeStore = this.getShapeStore(nonPointShapeIndex);
    const keyLocations$ = shapeStore('key_locations').$;

    return keyLocations$.pipe(
      debounceTime(0),
      map((keyLocations) =>
        Math.max(...keyLocations.map((k) => k.points.length)),
      ),
      distinctUntilChanged(),
      filter(
        (maxKeyLocationPointCount) =>
          maxKeyLocationPointCount > pointShapesIndexes.length,
      ),
      tap((maxKeyLocationPointCount) => {
        const newPointIndexes: number[] = [];
        this.store.batch(() => {
          for (
            let subpointIndex = pointShapesIndexes.length;
            subpointIndex < maxKeyLocationPointCount;
            subpointIndex++
          ) {
            newPointIndexes.push(
              this.createPointForShape(shapeStore.state(), subpointIndex).index,
            );
          }
          this.shapeGroupingService.groupShapes(
            groupType,
            mapAsKeys(
              [nonPointShapeIndex, ...pointShapesIndexes, ...newPointIndexes],
              () => true,
            ),
          );
        });
      }),
    );
  }

  private updatePoints$(
    nonPointShapeIndex: number,
    pointShapesIndexes: number[],
  ) {
    const shapeStore = this.getShapeStore(nonPointShapeIndex);
    const keyLocations$ = shapeStore('key_locations').$;

    return keyLocations$.pipe(
      debounceTime(0),
      map((nonPointShapeKeyLocations) =>
        Math.max(...nonPointShapeKeyLocations.map((k) => k.points.length)),
      ),
      filter(
        (maxKeyLocationPointCount) =>
          maxKeyLocationPointCount <= pointShapesIndexes.length &&
          !this.arePointsUpToDate(nonPointShapeIndex, pointShapesIndexes),
      ),
      tap(() => {
        const nonPointShapeKeyLocations = shapeStore.state().key_locations;
        this.store.batch(() => {
          pointShapesIndexes.forEach((pointShapeIndex, subpointIndex) => {
            const pointShapeKeyLocationsStore =
              this.getShapeStore(pointShapeIndex)('key_locations');
            pointShapeKeyLocationsStore.setUsing((pointShapeKeyLocations) =>
              nonPointShapeKeyLocations.map(
                (nonPointShapeKeyLocation, keyLocationIndex) => ({
                  ...nonPointShapeKeyLocation,
                  points: [
                    // fix me if you change this code
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
                    nonPointShapeKeyLocation.points[subpointIndex] ??
                      pointShapeKeyLocations[keyLocationIndex]?.points[0] ?? [
                        0, 0,
                      ],
                  ],
                  visibility: nonPointShapeKeyLocation.points[subpointIndex]
                    ? nonPointShapeKeyLocation.visibility
                    : 0,
                }),
              ),
            );
          });
        });
      }),
    );
  }

  private arePointsUpToDate(
    nonPointShapeIndex: number,
    pointShapesIndexes: number[],
  ) {
    const nonPointShape = this.getShapeStore(nonPointShapeIndex).state();
    const maxKeyLocationPointCount = Math.max(
      ...nonPointShape.key_locations.map((k) => k.points.length),
    );

    if (maxKeyLocationPointCount > pointShapesIndexes.length) {
      return false;
    }

    const pointShapes = pointShapesIndexes.map((index) =>
      this.getShapeStore(index).state(),
    );

    return pointShapes.every((pointShape, subpointIndex) => {
      if (
        pointShape.key_locations.length !== nonPointShape.key_locations.length
      ) {
        return false;
      }
      return pointShape.key_locations.every(
        (pointShapeKeyLocation, keyLocationIndex) => {
          const nonPointShapeKeyLocation =
            nonPointShape.key_locations[keyLocationIndex];
          if (
            // fix me if you change this code
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
            !nonPointShapeKeyLocation ||
            pointShapeKeyLocation.frame_number !==
              nonPointShapeKeyLocation.frame_number
          ) {
            return false;
          }
          const nonPointShapeSubpoint =
            nonPointShapeKeyLocation.points[subpointIndex];
          // fix me if you change this code
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
          if (!nonPointShapeSubpoint) {
            return pointShapeKeyLocation.visibility === 0;
          } else {
            return (
              pointShapeKeyLocation.visibility ===
                nonPointShapeKeyLocation.visibility &&
              isEqual(pointShapeKeyLocation.points[0], nonPointShapeSubpoint)
            );
          }
        },
      );
    });
  }

  private updateSubpoints$(
    nonPointShapeIndex: number,
    pointShapesIndexes: number[],
  ) {
    return merge(
      ...pointShapesIndexes.map(
        (pointShapeIndex) =>
          this.getShapeStore(pointShapeIndex)('key_locations').$,
      ),
    ).pipe(
      debounceTime(0),
      filter(
        () => !this.arePointsUpToDate(nonPointShapeIndex, pointShapesIndexes),
      ),
      tap(() => {
        const shapeInfo = this.outputAnnotationInfoStore.state();
        const frameNumbers = this.extractFrameNumbers(pointShapesIndexes);

        const newNonPointShapeKeyLocations: Array<
          ShapeLocationState<PointType>
        > = [];
        frameNumbers.forEach((frameNumber) => {
          const visibleKeyLocations = pointShapesIndexes
            .map((pointShapeIndex) =>
              // @ts-ignore -- fix me
              shapeInfo.shapes[pointShapeIndex].key_locations.find(
                matches({ frame_number: frameNumber }),
              ),
            )
            .filter(
              (pointShapeKeyLocation) =>
                pointShapeKeyLocation && pointShapeKeyLocation.visibility !== 0,
            );
          const points = visibleKeyLocations.map(
            (pointShapeKeyLocation) => pointShapeKeyLocation!.points[0],
          );
          const visibility = points.length
            ? visibleKeyLocations[0]!.visibility
            : 0;
          newNonPointShapeKeyLocations.push({
            frame_number: frameNumber,
            points: points.length
              ? points
              : newNonPointShapeKeyLocations[
                  newNonPointShapeKeyLocations.length - 1
                ].points,
            visibility,
          });
        });
        this.getShapeStore(nonPointShapeIndex)('key_locations').set(
          newNonPointShapeKeyLocations,
        );
      }),
    );
  }

  private extractFrameNumbers(pointShapesIndexes: number[]) {
    return Array.from(
      new Set(
        flatten(
          pointShapesIndexes.map((pointShapeIndex) =>
            this.getShapeStore(pointShapeIndex)
              .state()
              .key_locations.map((k) => k.frame_number),
          ),
        ),
      ),
    ).sort((a, b) => a - b);
  }

  private getShapeStore(shapeIndex: number) {
    const shapeStore = this.outputAnnotationInfoStore('shapes')(shapeIndex);
    assertStore<ShapeState>(
      shapeStore,
      'The shape must be defined in order to edit its subpoints.',
    );
    return shapeStore;
  }
}
